import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, MapPin, ExternalLink } from "lucide-react";
import { api, formatErr } from "../lib/api";
import { DEPARTMENTS } from "../lib/format";
import { PageHeader, EmptyState, StatusBadge } from "../components/Common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STAGES = [["applied", "Applied"], ["screening", "Screening"], ["interview", "Interview"], ["hr_round", "HR Round"], ["offered", "Offered"], ["hired", "Hired"], ["rejected", "Rejected"]];

const FormDialog = ({ open, onOpenChange, title, fields, initial, onSubmit, testid }) => {
  const [f, setF] = useState(initial);
  useEffect(() => {
    if (open) setF(initial);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async (e) => {
    e.preventDefault();
    if (await onSubmit(f)) onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-h-[90vh] overflow-y-auto" data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {fields.map(([k, l, type, opts]) => (
            <div key={k} className="space-y-1.5"><Label>{l}</Label>
              {type === "select" ? (
                <Select value={f[k] || undefined} onValueChange={(v) => setF({ ...f, [k]: v })}>
                  <SelectTrigger data-testid={`${testid}-${k}`}><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{opts.map(([v, lbl]) => <SelectItem key={v} value={v}>{lbl}</SelectItem>)}</SelectContent>
                </Select>
              ) : type === "textarea" ? (
                <Textarea value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`${testid}-${k}`} />
              ) : (
                <Input type={type || "text"} required={k === "title" || k === "name"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`${testid}-${k}`} />
              )}
            </div>
          ))}
          <Button type="submit" className="w-full bg-[#E08A1E] hover:bg-[#C87515] text-white" data-testid={`${testid}-submit`}>Save</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const CandidateCard = ({ c, onMove, onDelete }) => (
  <div className="bg-white border border-[#E5E5EA] rounded-xl p-3 space-y-2 hover:shadow-sm transition-shadow" data-testid={`candidate-card-${c.id}`}>
    <div className="flex justify-between gap-2">
      <div className="min-w-0"><div className="font-semibold text-sm truncate">{c.name}</div><div className="text-xs text-[#6E6E73] truncate">{c.email || c.phone}</div></div>
      <button onClick={() => onDelete(c)} className="text-neutral-400 hover:text-red-600" data-testid={`delete-candidate-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
    {/^https?:\/\//i.test(c.resume_url || "") && <a href={c.resume_url} target="_blank" rel="noreferrer" className="text-xs text-[#E08A1E] flex items-center gap-1"><ExternalLink className="h-3 w-3" />Resume</a>}
    <Select value={c.stage} onValueChange={(v) => onMove(c, v)}>
      <SelectTrigger className="h-8 text-xs" data-testid={`candidate-stage-${c.id}`}><SelectValue /></SelectTrigger>
      <SelectContent>{STAGES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

export default function Recruitment() {
  const [jobs, setJobs] = useState([]);
  const [sel, setSel] = useState(null);
  const [cands, setCands] = useState([]);
  const [dlg, setDlg] = useState(null);

  const loadJobs = () => api.get("/jobs").then((r) => { setJobs(r.data); setSel((s) => s || r.data[0]?.id || null); });
  const loadCands = () => sel && api.get("/candidates", { params: { job_id: sel } }).then((r) => setCands(r.data));
  useEffect(() => {
    loadJobs();
  }, []);
  useEffect(() => {
    loadCands();
  }, [sel]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = (fn, msg) => async (f) => {
    try { await fn(f); toast.success(msg); loadJobs(); loadCands(); return true; } catch (e) { toast.error(formatErr(e)); return false; }
  };
  const job = jobs.find((j) => j.id === sel);
  const move = (c, stage) => save((f) => api.put(`/candidates/${c.id}`, { ...c, stage }), "Candidate moved")();
  const delCand = (c) => save(() => api.delete(`/candidates/${c.id}`), "Candidate removed")();
  const toggleJob = () => save(() => api.put(`/jobs/${job.id}`, { ...job, status: job.status === "open" ? "closed" : "open" }), "Job updated")();
  const delJob = async () => { await save(() => api.delete(`/jobs/${job.id}`), "Job deleted")(); setSel(null); };

  return (
    <div data-testid="recruitment-page">
      <PageHeader eyebrow="Talent" title="Recruitment" subtitle="Manage openings and move candidates through the hiring pipeline."
        actions={<>
          <Button variant="outline" onClick={() => setDlg("candidate")} disabled={!job} data-testid="add-candidate-button" className="rounded-full bg-white"><Plus className="h-4 w-4 mr-1" />Candidate</Button>
          <Button onClick={() => setDlg("job")} data-testid="add-job-button" className="bg-[#E08A1E] hover:bg-[#C87515] text-white rounded-full"><Plus className="h-4 w-4 mr-1" />Job opening</Button>
        </>} />
      {!jobs.length ? <EmptyState text="No job openings yet. Create your first one." testid="jobs-empty" /> : (
        <>
          <div className="flex gap-3 overflow-x-auto pb-2 mb-6" data-testid="jobs-list">
            {jobs.map((j) => (
              <button key={j.id} onClick={() => setSel(j.id)} data-testid={`job-card-${j.id}`}
                className={`text-left shrink-0 w-64 rounded-2xl p-4 border transition-colors ${sel === j.id ? "bg-[#0A0807] text-white border-[#0A0807]" : "bg-white border-[#E5E5EA] hover:border-[#E08A1E]"}`}>
                <div className="flex justify-between items-start gap-2"><div className="font-bold truncate">{j.title}</div><StatusBadge status={j.status} /></div>
                <div className={`text-xs mt-1 ${sel === j.id ? "text-neutral-400" : "text-[#6E6E73]"}`}>{j.department} · {j.employment_type}</div>
                <div className={`text-xs mt-2 flex items-center gap-1 ${sel === j.id ? "text-neutral-400" : "text-[#6E6E73]"}`}><MapPin className="h-3 w-3" />{j.location || "—"} · {j.candidate_count} candidates</div>
              </button>
            ))}
          </div>
          {job && (
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <div className="text-sm text-[#6E6E73] max-w-2xl">{job.description || "No description"} · {job.openings} opening(s)</div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={toggleJob} data-testid="toggle-job-status-button">{job.status === "open" ? "Close job" : "Reopen job"}</Button>
                <Button size="sm" variant="outline" className="text-red-600" onClick={delJob} data-testid="delete-job-button"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          )}
          <div className="flex gap-3 overflow-x-auto pb-4" data-testid="pipeline-board">
            {STAGES.map(([v, l]) => {
              const items = cands.filter((c) => c.stage === v);
              return (
                <div key={v} className="shrink-0 w-60 bg-[#EDEDF0] rounded-2xl p-3" data-testid={`stage-column-${v}`}>
                  <div className="flex justify-between text-xs font-bold uppercase tracking-wider text-[#6E6E73] mb-3 px-1"><span>{l}</span><span>{items.length}</span></div>
                  <div className="space-y-2">{items.map((c) => <CandidateCard key={c.id} c={c} onMove={move} onDelete={delCand} />)}</div>
                </div>
              );
            })}
          </div>
        </>
      )}
      <FormDialog open={dlg === "job"} onOpenChange={(o) => !o && setDlg(null)} title="New job opening" testid="job-form"
        initial={{ title: "", department: "", location: "", employment_type: "Full-time", openings: 1, description: "", status: "open" }}
        fields={[["title", "Job title"], ["department", "Department", "select", DEPARTMENTS.map((d) => [d, d])], ["location", "Location"], ["employment_type", "Employment type", "select", ["Full-time", "Part-time", "Contract", "Internship"].map((x) => [x, x])], ["openings", "Openings", "number"], ["description", "Description", "textarea"]]}
        onSubmit={save((f) => api.post("/jobs", { ...f, openings: Number(f.openings) || 1 }), "Job opening created")} />
      <FormDialog open={dlg === "candidate"} onOpenChange={(o) => !o && setDlg(null)} title={`Add candidate · ${job?.title || ""}`} testid="candidate-form"
        initial={{ name: "", email: "", phone: "", resume_url: "", notes: "", stage: "applied" }}
        fields={[["name", "Full name"], ["email", "Email", "email"], ["phone", "Phone"], ["resume_url", "Resume link", "url"], ["notes", "Notes", "textarea"]]}
        onSubmit={save((f) => api.post("/candidates", { ...f, job_id: sel }), "Candidate added")} />
    </div>
  );
}
